import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchInstitution } from "../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Mail, Phone, MapPin, Globe, X, Building2, Eye, ExternalLink } from "lucide-react";
import { PrivateCourtSidebar } from "./PrivateCourtAd";

const maskEmail = (e) => {
  if (!e) return "";
  const [u, d] = e.split("@");
  if (!d) return e.slice(0, 2) + "•••";
  const uMask = u.length <= 2 ? u[0] + "•••" : u.slice(0, 2) + "•••" + u.slice(-1);
  return `${uMask}@${d}`;
};

const maskPhone = (p) => {
  if (!p) return "";
  const digits = p.replace(/\D/g, "");
  if (digits.length < 6) return "•••";
  return p.replace(digits.slice(2, -3), "•".repeat(Math.max(3, digits.length - 5)));
};

const ContactCard = ({ c }) => {
  const [revealed, setRevealed] = useState(false);
  const isRecovery = c.role?.toLowerCase().includes("recovery");
  const isLegal = c.role?.toLowerCase().includes("legal");
  const accent = isRecovery ? "#FF3333" : isLegal ? "#002FA7" : "#0A0A0A";
  return (
    <div
      data-testid={`contact-card-${(c.role || "").toLowerCase().replace(/\s+/g, "-")}`}
      className="border border-[#E5E7EB] p-5 bg-white hover:border-[#0A0A0A] transition-colors"
      style={{ userSelect: revealed ? "text" : "none", WebkitUserSelect: revealed ? "text" : "none" }}
      onContextMenu={(e) => !revealed && e.preventDefault()}
    >
      <div className="flex items-center justify-between mb-3">
        <span
          className="font-mono text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 border"
          style={{ color: accent, borderColor: accent }}
        >
          {c.role}
        </span>
      </div>
      <div className="font-display font-bold text-lg leading-tight">{c.name}</div>
      <div className="text-sm text-[#4B5563] mt-0.5">{c.designation}</div>

      {!revealed && (c.email || c.phone) && (
        <div className="mt-4 space-y-2 text-sm opacity-80">
          {c.email && (
            <div className="flex items-start gap-2 text-[#4B5563]">
              <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span className="font-mono text-xs tracking-wide">{maskEmail(c.email)}</span>
            </div>
          )}
          {c.phone && (
            <div className="flex items-start gap-2 text-[#4B5563]">
              <Phone className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span className="font-mono text-xs">{maskPhone(c.phone)}</span>
            </div>
          )}
          <button
            type="button"
            onClick={() => setRevealed(true)}
            data-testid={`reveal-${(c.role || "").toLowerCase().replace(/\s+/g, "-")}`}
            className="mt-3 inline-flex items-center gap-2 border border-[#0A0A0A] px-3 py-1.5 text-xs font-mono uppercase tracking-[0.15em] hover:bg-[#0A0A0A] hover:text-white transition-colors"
          >
            <Eye className="w-3.5 h-3.5" /> Reveal contact
          </button>
        </div>
      )}

      {revealed && (
        <div className="mt-4 space-y-2 text-sm">
          {c.email && (
            <a
              href={`mailto:${c.email}`}
              className="flex items-start gap-2 hover:text-[#002FA7] transition-colors break-all"
            >
              <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span className="font-mono text-xs">{c.email}</span>
            </a>
          )}
          {c.phone && (
            <a
              href={`tel:${c.phone}`}
              className="flex items-start gap-2 hover:text-[#002FA7] transition-colors"
            >
              <Phone className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span className="font-mono text-xs">{c.phone}</span>
            </a>
          )}
          {c.address && (
            <div className="flex items-start gap-2 text-[#4B5563]">
              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span className="text-xs leading-relaxed">{c.address}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const InstitutionDrawer = ({ id, open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!id || !open) return;
    setLoading(true);
    fetchInstitution(id)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id, open]);

  const roleOrder = ["Principal Nodal Officer", "Grievance Redressal Officer", "Recovery Head", "Legal Head"];
  const sortedContacts = (data?.contacts || []).slice().sort(
    (a, b) => roleOrder.indexOf(a.role) - roleOrder.indexOf(b.role)
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="institution-drawer"
        className="w-full sm:max-w-2xl rounded-none border-l-2 border-[#0A0A0A] p-0 overflow-y-auto bg-white"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>Institution Detail</SheetTitle>
          <SheetDescription>
            Contact details, registered address, nodal / grievance / recovery / legal heads for the selected institution.
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="p-8 font-mono text-xs uppercase tracking-[0.2em] text-[#4B5563]">
            Loading…
          </div>
        )}

        {data && (
          <>
            <div className="sticky top-0 z-10 bg-white border-b border-[#0A0A0A] px-6 md:px-8 py-5 flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 bg-[#0A0A0A] flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">
                    {data.type} · {data.category}
                  </div>
                  <h2
                    className="font-display font-black text-2xl md:text-3xl tracking-tighter leading-tight mt-1 truncate"
                    data-testid="drawer-institution-name"
                  >
                    {data.name}
                  </h2>
                </div>
              </div>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                data-testid="drawer-close-button"
                className="p-2 hover:bg-[#F8F9FA] transition-colors flex-shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 md:px-8 py-6">
              <div className="grid grid-cols-2 gap-px bg-[#E5E7EB] border border-[#E5E7EB] mb-8">
                {[
                  { k: "HQ City", v: data.hq_city },
                  { k: "HQ State", v: data.hq_state },
                  { k: "RBI License", v: data.rbi_license_no || "—" },
                  { k: "Short Name", v: data.short_name || "—" },
                ].map((f) => (
                  <div key={f.k} className="bg-white p-3">
                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#4B5563]">
                      {f.k}
                    </div>
                    <div className="font-mono text-sm mt-0.5 truncate">{f.v}</div>
                  </div>
                ))}
              </div>

              {data.website && (
                <a
                  href={data.website}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-[#002FA7] hover:underline mb-2 font-mono text-xs"
                  data-testid="drawer-website-link"
                >
                  <Globe className="w-3.5 h-3.5" />
                  {data.website.replace(/^https?:\/\//, "")}
                </a>
              )}
              {data.slug && (
                <div className="mb-6">
                  <Link
                    to={`/${data.type === "Bank" ? "bank" : "nbfc"}/${data.slug}`}
                    onClick={() => onOpenChange(false)}
                    className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-[#0A0A0A] border-b-2 border-[#0A0A0A] pb-1 hover:text-[#002FA7] hover:border-[#002FA7]"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Open full institution page
                  </Link>
                </div>
              )}

              <div className="mb-4 flex items-baseline justify-between">
                <h3 className="font-display font-bold text-xl tracking-tight">
                  Contacts <span className="text-[#4B5563]">({sortedContacts.length})</span>
                </h3>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">
                  {data.has_detailed_contacts ? "Escalation order" : "From RBI master list"}
                </span>
              </div>

              {!data.has_detailed_contacts && (
                <div
                  data-testid="drawer-unverified-notice"
                  className="mb-4 p-4 border-l-4 border-[#FF3333] bg-red-50 font-mono text-[11px] leading-relaxed text-red-900"
                >
                  <div className="font-bold uppercase tracking-[0.15em] mb-1">RBI basic entry</div>
                  This institution is listed in RBI's official master list but we have not yet
                  curated its Principal Nodal Officer, Recovery Head or Legal Head contacts.
                  Please contact the institution via the website or email below and verify any
                  detail before initiating formal action.
                </div>
              )}

              <div className="space-y-3">
                {sortedContacts.length === 0 ? (
                  <div className="p-6 border border-dashed border-[#D1D5DB] text-center text-sm text-[#4B5563]">
                    No contact on file. Visit the official website / email the institution directly.
                  </div>
                ) : sortedContacts.map((c, i) => (
                  <ContactCard key={`${c.role}-${i}`} c={c} />
                ))}
              </div>

              <div className="mt-8 p-4 border border-amber-300 bg-amber-50 font-mono text-[11px] leading-relaxed text-amber-900">
                <strong>Disclaimer:</strong> Contact details compiled from RBI public disclosures
                & institutional websites. Always verify at the source URL above before initiating
                legal or formal recovery communications.
              </div>

              <div className="mt-6">
                <PrivateCourtSidebar />
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
